import React, { memo } from 'react';
import CustomInput from 'components/CustomInput/CustomInput';
import DownloadMenu from 'components/DownloadMenu/DownloadMenu';
import Dropdown, { IDropdownItem } from 'components/Dropdown/Dropdown';
import CanPerform from 'components/CanPerform/CanPerform';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/components/tableHeaderStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { BodyS } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import HeaderButtons from './subcomponents/HeaderButtons';

// @ts-ignore
const useStyles = makeStyles(styles);

export interface IButton {
	text: string | React.ReactElement;
	onClick?: (event?: Event) => void;
	buttonProps?: object;
	action?: string;
	id?: string;
}

interface IDropdown {
	action: string;
	menu: React.ReactElement | string;
	items: IDropdownItem[];
}

interface ITableHeaderProps {
	setSearchInputValue?: (element: string) => void;
	searchInputValue?: string;
	exportToXLS?: () => void;
	exportToCSV?: () => void;
	buttons?: IButton | IButton[];
	text?: string;
	label?: string;
	dropdown?: IDropdown;
	stroke?: boolean;
	withBackground?: boolean;
	searchPlaceHolder?: string;
	searchType?: string
}

const TableHeader = ({
	setSearchInputValue,
	searchInputValue,
	searchPlaceHolder,
	searchType,
	exportToXLS,
	exportToCSV,
	buttons,
	label = translate('search'),
	dropdown,
	text,
	stroke,
	withBackground,
}: ITableHeaderProps) => {
	const classes = useStyles();

	const tableHeaderWrapperClasses = classNames({
		[classes.tableHeaderWrapper]: true,
		[classes.alignCenter]: !setSearchInputValue,
		[classes.withText]: text,
		[classes.withStroke]: stroke,
	});

	return (
		<div className={tableHeaderWrapperClasses}>
			<ConditionalRender
				show={!!text}
				onTrue={() => (
					<BodyS color="secondary-light" disableMargin>
						{text}
					</BodyS>
				)}
			/>
			<ConditionalRender
				show={!!setSearchInputValue}
				onTrue={() => (
					<div
						className={classNames(
							classes.searchWrapper,
							'search-component',
							withBackground ? classes.searchBackground : ''
						)}
					>
						<CustomInput
							labelText={!withBackground ? label : ''}
							formControlProps={{
								fullWidth: true,
							}}
							inputProps={{
								className: 'header-search-input',
								value: searchInputValue,
								type: searchType,
								onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
									event.preventDefault();
									if (setSearchInputValue) {
										setSearchInputValue(event.target.value);
									}
								},
								endAdornment: !withBackground && (
									<InputAdornment position="end">
										<SearchIcon className={classes.searchIcon} />
									</InputAdornment>
								),
								startAdornment: withBackground && (
									<InputAdornment position="start">
										<SearchIcon className={classes.searchIconLarge} />
									</InputAdornment>
								),
								placeholder: searchPlaceHolder || 'Search',
								disableUnderline: true,
							}}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={!!exportToXLS && !withBackground}
				onTrue={() => (
					<div className={classes.downloadWrapper}>
						<DownloadMenu
							key="download"
							items={[
								{
									name: 'XLSX',
									onClick: exportToXLS!,
								},
							]}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={!!exportToCSV}
				onTrue={() => (
					<div className={classes.downloadWrapper}>
						<DownloadMenu
							key="download"
							items={[
								{
									name: 'CSV',
									onClick: exportToCSV!,
								},
							]}
						/>
					</div>
				)}
			/>
			<HeaderButtons buttons={buttons} />
			<ConditionalRender
				show={!!dropdown}
				onTrue={() => (
					<CanPerform
						action={dropdown?.action}
						yes={
							<div className={classes.wrapper}>
								<Dropdown
									menu={dropdown?.menu}
									items={dropdown?.items}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
								/>
							</div>
						}
					/>
				)}
			/>
		</div>
	);
};

export default memo(TableHeader);
